import { useEffect, useRef } from 'react'
import { isInsideTaskDetails } from '../../../utils/taskDetailsFocus'

interface UseCanvasZoomShortcutOptions {
  /**
   * Nur der sichtbare Tab darf das Kürzel abfangen. Inaktive Tabs bleiben
   * hinter `display: none` gemountet (CLAUDE.md TabStack-Falle) — ohne dieses
   * Tor stähle ein Hintergrund-Projekt mit offenem Canvas das ⇧0 des
   * sichtbaren.
   */
  enabled: boolean
  /** Stellt die 1:1-Ansicht her (Canvas-Zoomstufe 1, Ausschnitt bleibt). */
  onResetZoom: () => void
}

/**
 * ⇧0 **und** ⌘/⌃0 stellen im Projekt-Canvas die **1:1-Ansicht** her —
 * Zoomstufe 1, ohne den Ausschnitt zu verschieben.
 *
 * **Zwei Wege für ⌘0, und beide werden gebraucht:**
 *
 * | | |
 * |---|---|
 * | Web | der Tastendruck unten — dort gibt es kein Anwendungsmenü |
 * | Electron | die Nachricht `reset-canvas-zoom` aus `menuService.ts` |
 *
 * Unter macOS verbraucht das native Menü sein Tastenkürzel, bevor der Renderer
 * es sieht (`performKeyEquivalent:` läuft vor der Responder-Kette). Ein
 * Listener allein feuerte dort also nie — deshalb schickt der Menüpunkt
 * „Tatsächliche Größe" die Nachricht mit, zusätzlich zum App-Zoom, den er
 * weiterhin zurücksetzt.
 *
 * Damit teilen sich zwei Zoombegriffe ein Kürzel (CONTEXT.md, „1:1-Ansicht vs.
 * Tatsächliche Größe"). Getrennt bleiben sie in Name und Wirkung: ⌘0 setzt den
 * App-Zoom zurück UND holt den Canvas auf 1:1; ⇧0 rührt allein den Canvas an.
 *
 * @example
 * useCanvasZoomShortcut({ enabled: isActiveTab, onResetZoom: handleResetZoom })
 */
export function useCanvasZoomShortcut({ enabled, onResetZoom }: UseCanvasZoomShortcutOptions) {
  // Über ein Ref gelesen, damit ein bei jedem Render neu erzeugter Rückruf den
  // Listener nicht ab- und wieder anmeldet.
  const onResetZoomRef = useRef(onResetZoom)
  onResetZoomRef.current = onResetZoom

  useEffect(() => {
    if (!enabled) return

    const handleKeyDown = (e: KeyboardEvent) => {
      // `code` statt `key`: auf deutschem Layout liefert ⇧0 ein „=", die
      // physische Taste heißt aber überall `Digit0`. Dasselbe Muster wie in
      // useProjectKeyboardShortcuts (`e.code === 'KeyN'`).
      if (e.code !== 'Digit0') return
      // ⌥ gehört keiner bekannten Geste. ⌘⇧0 ebenso wenig — wer beide
      // Modifikatoren hält, meint etwas anderes als eine der zwei Gesten.
      if (e.altKey) return
      const modifier = e.metaKey || e.ctrlKey
      if (modifier === e.shiftKey) return
      // Im Text einer Notiz ist ⇧0 auf deutschem Layout schlicht ein „=".
      const tag = (e.target as HTMLElement | null)?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA') return
      // Die Tasten der **Aufgaben-Details** gehören ihnen (CONTEXT.md): Bei
      // geteiltem Canvas liegen sie als Slide-over darüber, und der Fokus steht
      // dort meist auf einem KNOPF — am INPUT/TEXTAREA-Wächter oben vorbei.
      if (isInsideTaskDetails(e.target)) return

      e.preventDefault()
      onResetZoomRef.current()
    }

    window.addEventListener('keydown', handleKeyDown)
    // Der Weg für Electron: Dort verbraucht das Menü das ⌘0 (siehe oben).
    const unsubscribe = window.api?.receive?.('reset-canvas-zoom', () => {
      onResetZoomRef.current()
    })

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      unsubscribe?.()
    }
  }, [enabled])
}
